import React from "react";
import DocsPageHeader from "../../../components/dashboard/common/Documentation/DocsPageHeader";
import SearchHighlighter from "../../../components/dashboard/common/Documentation/SearchHighlighter";

const ProgramEngineering = () => {
  return (
    <SearchHighlighter>
      <DocsPageHeader 
        number="5" 
        title="Program Engineering & Deployment" 
        description="A Program is the container that links assets, targets, scope, credentials, and rules of engagement into a deployable testing engagement. Programs are created using a guided 7-step wizard."
      />


      <h2 id="scheduling" className="text-xl font-bold text-black mt-10 mb-4">5.1 Scheduling Patterns & Constraints</h2>
      <p className="mb-6 text-slate-600 leading-relaxed">The platform enforces strict scheduling boundaries based on your enterprise subscription level. These are hard-coded in the wizard to prevent resource over-utilization.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="rounded-2xl border border-[#eaeaea] p-6 bg-[#fafafa]/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg text-lg">📅</div>
            <h3 className="font-bold text-black">One-off Engagement</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">A single, time-bound pentest execution. Best for compliance checkpoints or specific release testing.</p>
          <ul className="text-[11px] space-y-2 list-none p-0">
            <li className="flex items-center gap-2 text-slate-600">
              <span className="w-1 h-1 rounded-full bg-blue-400" />
              <span>Restricted to <b>One-off / Pilot</b> tiers.</span>
            </li>
            <li className="flex items-center gap-2 text-slate-600">
              <span className="w-1 h-1 rounded-full bg-blue-400" />
              <span>Uses <b>Expected Dates</b> (Subject to Admin review).</span>
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-emerald-100 p-6 bg-emerald-50/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg text-lg">🔄</div>
            <h3 className="font-bold text-black font-black">Continuous / Recurring</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">Ongoing testing or recurring cycles. Manage frequency and run individual tests manually or via API.</p>
          <ul className="text-[11px] space-y-2 list-none p-0">
            <li className="flex items-center gap-2 text-slate-600">
              <span className="w-1 h-1 rounded-full bg-emerald-400" />
              <span>Available for <b>Enterprise / Premium</b> tiers.</span>
            </li>
            <li className="flex items-center gap-2 text-slate-600">
              <span className="w-1 h-1 rounded-full bg-emerald-400" />
              <span>Automatic target enrollment and cycle management.</span>
            </li>
          </ul>
        </div>
      </div>

      <h2 id="wizard" className="text-xl font-bold text-black mt-12 mb-4">5.2 The 7-Step Deployment Wizard</h2>
      <p className="mb-6 text-slate-600">The wizard ensures every configuration decision is made before submission. Data is persisted to `localStorage` automatically to prevent progress loss.</p>

      <div className="space-y-4 mb-12">
        {[
          { step: 1, name: "Identity", desc: "Define program title and scheduling strategy. One-off dates are considered 'Expectations' until approved." },
          { step: 2, name: "Asset Selection", desc: "Choose your primary asset. Assets locked in active comprehensive cycles are excluded." },
          { step: 3, name: "Scope Engineering", desc: "Attach targets. Platform runs an automated conflict check against overlapping schedules." },
          { step: 4, name: "Context & Focus", desc: "Markdown-enabled field for business priorities. Provide guidance on PII or critical functions." },
          { step: 5, name: "Credentials", desc: "Link Vault credentials. Handshake ensures researchers have valid access tokens." },
          { step: 6, name: "ROE", desc: "Define testing boundaries, blackout windows, and prohibited actions via custom ROE fields." },
          { step: 7, name: "Review & Launch", desc: "Final technical summary and readiness check before submitting for CTB Admin review." },
        ].map(item => (
          <div key={item.step} className="flex gap-4 p-4 rounded-xl border border-[#eaeaea] bg-white group hover:border-[#0070f3]/20 transition-all">
            <div className="w-10 h-10 rounded-lg bg-[#fafafa] flex items-center justify-center font-black text-slate-400 text-sm border border-[#eaeaea] group-hover:text-[#0070f3] group-hover:border-[#0070f3]/20 shrink-0">
              {item.step}
            </div>
            <div>
              <h4 className="font-bold text-black text-sm">{item.name}</h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 id="safety-checks" className="text-xl font-bold text-black mt-12 mb-4">5.3 Conflict Detection & Persistence</h2>
      
      <div className="rounded-2xl border-l-4 border-amber-400 bg-amber-50/50 p-6 my-8">
        <h4 className="font-bold text-amber-900 flex items-center gap-2 mb-3">
          <span>⚠️</span> Technical Conflict States
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs leading-relaxed">
          <div>
            <span className="font-bold text-amber-800">Occupied in Timeframe:</span>
            <p className="text-amber-700/80 mt-1">The `check-conflicts` API has detected that this target is already scheduled for another run during your selected window.</p>
          </div>
          <div>
            <span className="font-bold text-amber-800">Already in Cycle:</span>
            <p className="text-amber-700/80 mt-1">This target is currently being tested in an active comprehensive cycle and cannot be booked in parallel.</p>
          </div>
        </div>
      </div>

      <div className="mt-8 p-6 rounded-lg border border-[#eaeaea] bg-[#fafafa]">
        <span className="font-bold text-black">Draft Persistence:</span> The platform uses a local drafting system (`ctb_program_v2_draft`). If you navigate away or lose connection, the wizard will recover your progress upon your next visit to the Program Editor.
      </div>
    </SearchHighlighter>
  );
};

export default ProgramEngineering;
